import React, { useState, useRef } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { callApi } from '@/utils/callApi';

export default function FileUpload({ 
// Set accept="*/*" for all file types, or e.g. accept="image/*" for images or e.g. accept=".jpg,.jpeg,.png" for multiple file extensions.
  endpoint, 
  onUploadSuccess,
  fileTypeLabel = 'PDF',
  accept = '.pdf',
  successMessage,
  height = '150px' 
}) {
  const { instance, accounts } = useMsal();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text: string }
  const fileInputRef = useRef(null);

  const acceptsFile = (selectedFile) => {
    if (!accept || accept === '*/*') return true;

    const acceptedTypes = accept
      .split(',')
      .map(type => type.trim().toLowerCase())
      .filter(Boolean);

    if (acceptedTypes.length === 0) return true;

    const fileName = selectedFile.name.toLowerCase();
    const fileMime = selectedFile.type.toLowerCase();

    return acceptedTypes.some(type => {
      if (type.startsWith('.')) return fileName.endsWith(type);
      if (type.endsWith('/*')) return fileMime.startsWith(type.slice(0, -1));
      return fileMime === type;
    });
  };

  const handleFile = async (fileList) => {
    const selectedFile = fileList[0]; // Just take the first file
    setFile(selectedFile);
    setMessage(null); // Clear any previous messages

    // Immediately upload (or let user click to upload)
    await uploadFile(selectedFile);
  };

  const uploadFile = async (file, maxRetries = 3) => {
    if (!file) {
      console.log('[UPLOAD] No file provided');
      return;
    }

    if (!acceptsFile(file)) {
      setMessage({
        type: 'error',
        text: `Please select ${fileTypeLabel ? `a valid ${fileTypeLabel}` : 'a valid file'}.`,
      });
      return;
    }

    console.log('[UPLOAD] Starting upload for file:', {
      name: file.name,
      size: file.size,
      type: file.type,
      endpoint
    });
    
    setUploading(true);
    setMessage(null);
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`[UPLOAD] Attempt ${attempt}/${maxRetries}`);
        
        // Using FormData for multipart upload
        const formData = new FormData();
        formData.append('file', file); // Backend expects IFormFile with name 'file'
        
        console.log('[UPLOAD] FormData created, file field name:', 'file');
        console.log(`[UPLOAD] Calling endpoint: PUT ${endpoint} with MSAL auth`);
        
        // Use callApi with MSAL authentication (callApi detects FormData and skips Content-Type)
        const response = await callApi(
          endpoint,
          {
            method: 'PUT',
            body: formData,
          },
          instance,
          accounts
        );
        
        console.log('[UPLOAD] Response received - SUCCESS!');
        console.log('[UPLOAD] Response:', response);
        
        console.log(`[UPLOAD] ${fileTypeLabel} upload successful!`);
        await onUploadSuccess?.();

        setUploading(false);
        fileInputRef.current.value = '';
        setMessage({ 
          type: 'success', 
          text: successMessage || `${fileTypeLabel} uploaded successfully: ${file.name}` 
        });
        
        // Clear message and file state after 4 seconds
        setTimeout(() => {
          setMessage(null);
          setFile(null);
        }, 4000);
        
        return; // Success - exit the retry loop
        
      } catch (error) {
        console.error(`[UPLOAD] Attempt ${attempt} failed:`, error.message);
        console.error('[UPLOAD] Full error:', error);
        
        if (attempt === maxRetries) {
          console.error('[UPLOAD] Upload failed after all retries');
          setUploading(false);
          setMessage({ 
            type: 'error', 
            text: `Upload failed: ${error.message}` 
          });
          throw error; // Final attempt failed
        }
        
        const waitTime = attempt * 1000;
        console.log(`[UPLOAD] Waiting ${waitTime}ms before retry...`);
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  };
  
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files);
    }
  };

  const handleClick = () => {
  console.log('[UPLOAD] Upload area clicked');
  fileInputRef.current?.click();
};

const handleFileInputChange = (event) => {
  console.log('[UPLOAD] Input changed:', event.target.files);

  if (event.target.files?.[0]) {
    handleFile(event.target.files);
  }
};
  
  return (
    <>
     <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onClick={(event) => {
          event.currentTarget.value = '';
        }}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      <div 
        style={{
          border: "1px solid #dee2e6", 
          borderRadius: "6px", 
          padding: "20px", 
          textAlign: "center", 
          height: height, 
          cursor: "pointer", 
          marginTop: "3rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "var(--font-xl)",
        }}
        className={`upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
      >
      {file ? `Selected: ${file.name}` : `Drag ${fileTypeLabel} here or click to upload`}
      </div>
      <div>
        {uploading && <Spinner animation="border" size="sm" className="mt-2" />}
      </div>
      {message && (
        <Alert variant={message.type === 'success' ? 'success' : 'danger'} className="mt-2">
          {message.text}
        </Alert>
      )}
    </>
  );
};
